import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Building2, History, Loader2, LogIn, LogOut, ShieldCheck, Users } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { login, getMe, getToken, clearSession } from "../lib/metalApi";
import { StoricoTab, prefetchStorico, clearStoricoCache } from "./reserved/StoricoTab";
import { FornitoriTab } from "./reserved/FornitoriTab";
import { AdminTab } from "./reserved/AdminTab";
import { AziendeTab } from "./reserved/AziendeTab";

const EASE = [0.16, 1, 0.3, 1];

const FIELD_CLS =
  "h-12 w-full rounded-2xl border border-[#342C55] bg-[#17122E] px-4 font-manrope text-sm text-[#F5F5F5] placeholder:text-[#8B87A8] focus:border-[#34D399] focus:outline-none";

const TopBar = ({ user, onLogout }) => {
  const { t } = useLanguage();
  return (
    <div className="sticky top-0 z-40 border-b border-[#342C55] bg-[#17122E]/95 backdrop-blur">
      <div className="mx-auto flex h-[68px] max-w-5xl items-center justify-between px-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#34D399]/15 font-bebas text-sm text-[#34D399]">
            MC
          </span>
          <div>
            <p className="font-bebas text-lg leading-none tracking-[0.14em] text-[#F5F5F5]">
              METAL-CLASS
            </p>
            <p className="font-manrope text-[11px] text-[#8B87A8]">{user.company_name}</p>
          </div>
        </div>
        <button
          data-testid="reserved-logout-button"
          onClick={onLogout}
          className="flex items-center gap-2 rounded-xl border border-[#342C55] px-3.5 py-2 font-manrope text-xs font-semibold text-[#8B87A8] transition-colors duration-300 hover:border-[#F87171] hover:text-[#F87171]"
        >
          <LogOut className="h-4 w-4" />
          {t.reserved.logout}
        </button>
      </div>
    </div>
  );
};

const TabBar = ({ tab, setTab, isAdmin, isSuper }) => {
  const { t } = useLanguage();
  const tabs = [
    { key: "storico", icon: History, label: t.reserved.tabs.storico },
    { key: "fornitori", icon: Users, label: t.reserved.tabs.fornitori },
    ...(isSuper ? [{ key: "aziende", icon: Building2, label: t.reserved.tabs.aziende }] : []),
    ...(isAdmin ? [{ key: "admin", icon: ShieldCheck, label: t.reserved.tabs.admin }] : []),
  ];
  return (
    <nav
      data-testid="reserved-tabbar"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-[#342C55] bg-[#1D1838]/95 backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl">
        {tabs.map(({ key, icon: Icon, label }) => (
          <button
            key={key}
            data-testid={`reserved-tab-${key}`}
            onClick={() => setTab(key)}
            className={`flex flex-1 flex-col items-center gap-1 py-3 transition-colors duration-300 ${
              tab === key ? "text-[#34D399]" : "text-[#8B87A8] hover:text-[#F5F5F5]"
            }`}
          >
            <Icon className="h-5 w-5" />
            <span className="font-manrope text-[11px] font-semibold">{label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

const LoginCard = ({ onSuccess }) => {
  const { t } = useLanguage();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const result = await login(username.trim(), password);
      prefetchStorico();
      onSuccess(result.user ?? (await getMe()));
    } catch (err) {
      setError(err.message === "session_expired" ? t.reserved.errorGeneric : err.message || t.reserved.errorGeneric);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#17122E] px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="w-full max-w-md"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#34D399]/15 font-bebas text-xl text-[#34D399]">
            MC
          </span>
          <h1 className="font-bebas text-4xl uppercase tracking-tight text-[#F5F5F5] md:text-5xl">
            {t.reserved.heading}
          </h1>
          <p className="mt-3 font-manrope text-sm leading-relaxed text-[#8B87A8]">{t.reserved.sub}</p>
        </div>

        <form
          data-testid="reserved-login-form"
          onSubmit={submit}
          className="rounded-2xl border border-[#342C55] bg-[#221D3F] p-7"
        >
          <div className="space-y-4">
            <div>
              <span className="mb-2 block font-manrope text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8B87A8]">
                {t.reserved.username}
              </span>
              <input
                data-testid="reserved-username-input"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={FIELD_CLS}
              />
            </div>
            <div>
              <span className="mb-2 block font-manrope text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8B87A8]">
                {t.reserved.password}
              </span>
              <input
                data-testid="reserved-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={FIELD_CLS}
              />
            </div>
            {error && (
              <p data-testid="reserved-error" className="rounded-xl border border-[#F87171]/40 bg-[#F87171]/10 px-4 py-3 font-manrope text-sm text-[#F87171]">
                {error}
              </p>
            )}
            <button
              data-testid="reserved-submit-button"
              type="submit"
              disabled={busy}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#34D399] font-manrope text-sm font-bold text-[#17122E] transition-colors duration-300 hover:bg-[#2BBD8C] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              {busy ? t.reserved.loggingIn : t.reserved.submit}
            </button>
          </div>
        </form>
        <p className="mt-6 text-center">
          <a href="/" className="font-manrope text-xs text-[#8B87A8] transition-colors duration-300 hover:text-[#34D399]">
            ← metal-class.com
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default function ReservedArea() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("storico");

  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    getMe()
      .then((u) => {
        prefetchStorico();
        setUser(u);
      })
      .catch(() => clearSession())
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    clearSession();
    clearStoricoCache();
    setUser(null);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#17122E]">
        <Loader2 className="h-6 w-6 animate-spin text-[#34D399]" />
      </div>
    );
  }

  if (!user) return <LoginCard onSuccess={(u) => { prefetchStorico(); setUser(u); }} />;

  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const isSuper = user.role === "super_admin";

  return (
    <div className="min-h-screen bg-[#17122E] pb-24 text-[#F5F5F5]">
      <TopBar user={user} onLogout={logout} />
      <main className="mx-auto max-w-5xl px-5 py-8" data-testid="reserved-dashboard">
        {/* Tutte le schede restano sempre montate (nascoste via CSS): cambio
            scheda istantaneo, nessun ricaricamento, stato conservato */}
        <div className={tab === "storico" ? "" : "hidden"}>
          <StoricoTab isAdmin={isAdmin} />
        </div>
        <div className={tab === "fornitori" ? "" : "hidden"}>
          <FornitoriTab />
        </div>
        {isSuper && (
          <div className={tab === "aziende" ? "" : "hidden"}>
            <AziendeTab />
          </div>
        )}
        {isAdmin && (
          <div className={tab === "admin" ? "" : "hidden"}>
            <AdminTab currentUser={user} />
          </div>
        )}
      </main>
      <TabBar tab={tab} setTab={setTab} isAdmin={isAdmin} isSuper={isSuper} />
    </div>
  );
}
